/**
 * useLiveBlock.ts
 *
 * Live-querying hook for a single block by ID.
 * Re-renders automatically when that specific block changes in IndexedDB.
 *
 * Replaces: blockContext.getBlock(id) used inside components
 *
 * Usage:
 *   const block = useLiveBlock(blockId)
 */

"use client";

import { useLiveQuery } from "dexie-react-hooks";
import { appDB } from "../db/client";
import type { BlockRecord } from "../db/schema";

/**
 * Returns a single block record from IndexedDB, live-updating.
 * Returns undefined while loading or if block does not exist.
 */
export function useLiveBlock(blockId: string | null | undefined): BlockRecord | undefined {
  return useLiveQuery(
    () => {
      if (!blockId) return undefined;
      return appDB.blocks.get(blockId);
    },
    [blockId]
  );
}

/**
 * Returns a single block's value field, live-updating.
 * Useful for reading title, icon, content, etc. without receiving the full record.
 */
export function useLiveBlockValue<T = any>(
  blockId: string | null | undefined
): T | undefined {
  const block = useLiveBlock(blockId);
  return block?.value as T | undefined;
}
